import { useEffect } from 'react';
import { useTour } from '../../../contexts/TourContext';
import { claimDriver, releaseDriver } from '../tourDriver';
import { waitForAnchor } from '../waitForAnchor';

// Spotlights the one real control a step is about, using driver.js — it owns the
// overlay-with-a-hole, popover placement and cleanup.
// Loaded on demand so it never lands in the bundle for users not touring.
const PageSpotlight = () => {
  const { active, phase, current } = useTour();
  const anchor = active && phase === 'page' ? current?.pageAnchor : undefined;
  const title = current?.tipTitle;
  const task = current?.task;

  useEffect(() => {
    if (!anchor) return undefined;

    const owner = Symbol('page-spotlight');
    let cancelled = false;
    let acted = false;
    let wait: ReturnType<typeof waitForAnchor> | null = null;
    let el: HTMLElement | null = null;
    let onAct: (() => void) | null = null;
    let watchdog: number | undefined;

    const teardown = () => {
      if (el && onAct) el.removeEventListener('click', onAct);
      el = null;
      onAct = null;
      releaseDriver(owner);
    };

    const arm = () => {
      if (cancelled || acted) return;
      wait = waitForAnchor(anchor, async (found) => {
        if (cancelled || acted) return;
        el = found;
        const drv = await claimDriver(owner, { showButtons: [] });
        if (cancelled || acted || !drv) return;

        drv.highlight({ element: found, popover: { title: title || '', description: task || '', side: 'bottom', align: 'start' } });

        // The moment they act, get out of the way: whatever the button opens
        // (drawer, modal) has to be fully usable, close button included.
        onAct = () => {
          acted = true;
          window.clearInterval(watchdog);
          teardown();
        };
        found.addEventListener('click', onAct, { once: true });

        // Tables re-render after their data loads, which swaps the button for a
        // fresh node and leaves the highlight pinned to a detached element —
        // that is the spotlight "dropping off and never coming back". Re-arm on
        // the real replacement instead.
        window.clearInterval(watchdog);
        watchdog = window.setInterval(() => {
          if (cancelled || acted || !el) return;
          if (!el.isConnected) {
            teardown();
            arm();
          }
        }, 400);
      });
    };

    arm();

    return () => {
      cancelled = true;
      window.clearInterval(watchdog);
      wait?.cancel();
      teardown();
    };
  }, [anchor, title, task]);

  return null;
};

export default PageSpotlight;
